// Token Management Utilities
// Extracted from BattleUI.js for reusability across the app
import { API_URL } from '../config/apiConfig';

let inFlightRefresh = null;
let rateLimitedUntilMs = 0;

function readStoredAccessToken() {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  return localStorage.getItem('token');
}

function readStoredRefreshToken() {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  return localStorage.getItem('refreshToken');
}

function writeStoredAccessToken(token) {
  if (typeof localStorage === 'undefined') {
    return;
  }
  if (!token) {
    throw new Error('Cannot store empty access token');
  }
  localStorage.setItem('token', token);
}

function parseRetryAfterMs(response) {
  const header = response.headers?.get?.('retry-after');
  const headerSec = header != null ? Number.parseInt(header, 10) : NaN;
  if (Number.isFinite(headerSec) && headerSec > 0) {
    return headerSec * 1000;
  }
  return 60 * 1000;
}

function usableStoredAccessToken() {
  const stored = readStoredAccessToken();
  if (stored && isTokenValid(stored)) {
    return stored;
  }
  return null;
}

/**
 * Get token expiry time from JWT
 * @param {string} token - JWT token
 * @returns {number} - Expiry time in milliseconds
 */
export function getTokenExpiry(token) {
  if (!token) {
    throw new Error('Token is required to read expiry');
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) {
      throw new Error('Token payload is missing exp');
    }
    return payload.exp * 1000;
  } catch (error) {
    console.error('Error parsing token:', error);
    throw new Error(`Unable to read token expiry: ${error.message}`);
  }
}

/**
 * Check if token needs refresh (less than 5 minutes left)
 * @param {string} token - JWT token
 * @returns {boolean} - True if token needs refresh
 */
export function needsTokenRefresh(token) {
  if (!token) {
    return true;
  }
  try {
    const expiry = getTokenExpiry(token);
    const timeUntilExpiry = expiry - Date.now();
    return timeUntilExpiry < (5 * 60 * 1000);
  } catch (error) {
    console.error('Token refresh check failed:', error);
    return true;
  }
}

/**
 * Return a usable access token, refreshing over the network only when needed.
 * Concurrent callers share one in-flight request. A 429 does not evict a still-valid
 * access token — tutorial / battle actions must keep working through the rate-limit window.
 * @param {string} [refreshTokenValue] - Refresh token (defaults to localStorage)
 * @returns {Promise<string>} - Usable access token
 */
export async function refreshToken(refreshTokenValue) {
  const storedAccess = usableStoredAccessToken();
  if (storedAccess && !needsTokenRefresh(storedAccess)) {
    return storedAccess;
  }

  if (Date.now() < rateLimitedUntilMs) {
    if (storedAccess) {
      return storedAccess;
    }
    throw new Error('Token refresh failed: rate limited and access token is expired');
  }

  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  const refreshCredential = refreshTokenValue || readStoredRefreshToken();
  if (!refreshCredential) {
    if (storedAccess) {
      return storedAccess;
    }
    throw new Error('No refresh token available');
  }

  inFlightRefresh = performRefresh(refreshCredential)
    .finally(() => {
      inFlightRefresh = null;
    });

  return inFlightRefresh;
}

async function performRefresh(refreshCredential) {
  const response = await fetch(`${API_URL}/refresh-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refreshToken: refreshCredential })
  });

  if (response.status === 429) {
    rateLimitedUntilMs = Date.now() + parseRetryAfterMs(response);
    const storedAccess = usableStoredAccessToken();
    if (storedAccess) {
      console.warn('Token refresh rate-limited; continuing with still-valid access token');
      return storedAccess;
    }
    throw new Error('Token refresh failed: rate limited and access token is expired');
  }

  if (!response.ok) {
    throw new Error(`Token refresh failed: HTTP ${response.status}`);
  }

  const data = await response.json();
  const newToken = data.token || data.accessToken;
  if (!newToken) {
    throw new Error('Token refresh failed: response did not include a token');
  }
  writeStoredAccessToken(newToken);
  return newToken;
}

/**
 * Check if token is valid (not expired)
 * @param {string} token - JWT token
 * @returns {boolean} - True if token is valid
 */
export function isTokenValid(token) {
  if (!token) return false;
  try {
    const expiry = getTokenExpiry(token);
    return Date.now() < expiry;
  } catch (error) {
    console.error('Token validity check failed:', error);
    return false;
  }
}

/**
 * Get time until token expires in minutes
 * @param {string} token - JWT token
 * @returns {number} - Minutes until expiry
 */
export function getTimeUntilExpiry(token) {
  const expiry = getTokenExpiry(token);
  const timeUntilExpiry = expiry - Date.now();
  return Math.max(0, Math.ceil(timeUntilExpiry / (60 * 1000)));
}
